import React from 'react';
import { CampaignRequirements } from '@/types/campaign';
import { Calendar, Users, DollarSign, Target } from 'lucide-react';
import { format } from 'date-fns';

interface RequirementsPreviewProps {
  requirements: CampaignRequirements;
}

export const RequirementsPreview: React.FC<RequirementsPreviewProps> = ({ requirements }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div className="grid grid-cols-2 gap-3 mt-3">
      <div className="flex flex-col p-3 rounded-lg bg-neutral-50 border border-neutral-100/80 transition-colors hover:bg-neutral-100 hover:border-neutral-200">
        <span className="text-xs text-neutral-500 flex items-center gap-1.5 font-medium mb-1">
          <DollarSign className="w-3.5 h-3.5" />
          Budget
        </span>
        <span className="text-sm font-semibold text-neutral-900">{formatCurrency(requirements.budget_usd)}</span>
      </div>

      <div className="flex flex-col p-3 rounded-lg bg-neutral-50 border border-neutral-100/80 transition-colors hover:bg-neutral-100 hover:border-neutral-200">
        <span className="text-xs text-neutral-500 flex items-center gap-1.5 font-medium mb-1">
          <Calendar className="w-3.5 h-3.5" />
          Timeline
        </span>
        <span className="text-sm font-semibold text-neutral-900 truncate">
          {formatDate(requirements.campaign_start_date)} - {formatDate(requirements.campaign_end_date)}
        </span>
      </div>

      <div className="flex flex-col p-3 rounded-lg bg-neutral-50 border border-neutral-100/80 transition-colors hover:bg-neutral-100 hover:border-neutral-200 col-span-2 sm:col-span-1">
        <span className="text-xs text-neutral-500 flex items-center gap-1.5 font-medium mb-1">
          <Users className="w-3.5 h-3.5" />
          Target Audience
        </span>
        <span className="text-sm font-medium text-neutral-900 truncate" title={requirements.target_audience.join(', ')}>
          {requirements.target_audience.length > 0
            ? requirements.target_audience.slice(0, 2).join(', ') + (requirements.target_audience.length > 2 ? ` +${requirements.target_audience.length - 2}` : '')
            : 'Not specified'}
        </span>
      </div>

      <div className="flex flex-col p-3 rounded-lg bg-neutral-50 border border-neutral-100/80 transition-colors hover:bg-neutral-100 hover:border-neutral-200 col-span-2 sm:col-span-1">
        <span className="text-xs text-neutral-500 flex items-center gap-1.5 font-medium mb-1">
          <Target className="w-3.5 h-3.5" />
          Goals
        </span>
        <span className="text-sm font-medium text-neutral-900 truncate" title={requirements.success_metrics.join(', ')}>
          {requirements.success_metrics.length > 0
            ? requirements.success_metrics.slice(0, 2).join(', ') + (requirements.success_metrics.length > 2 ? ` +${requirements.success_metrics.length - 2}` : '')
            : 'Not specified'}
        </span>
      </div>
    </div>
  );
};
